'use client';

import { Play } from 'lucide-react';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import RespImage from '@/app/_components/core/resp-image';
import { VideoControls } from '@/app/_components/shared/video/components/video-controls';
import type { VideoPlayerHandle, VideoPlayerState } from '@/app/_components/shared/video/video.types';
import { useGlobalVideoPause } from '@/app/_hooks/use-global-video-pause';
import { cn, emptyToUndefined } from '@/app/_lib/utils';
import type { SmartVideoProps } from './smart-video.types';

export function SelfHostedPlayer({
  video,
  previewImage,
  title,
  imageSizes,
  priority = false,
  isActive = true,
  autoPlay = false,
  onPlay,
  trackingPrefix,
}: SmartVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPoster, setShowPoster] = useState(true);
  const hasPoster = previewImage && Array.isArray(previewImage.srcsetCandidates) && previewImage.srcsetCandidates.length > 0;

  // When no poster image: force video to render first frame (needed for Safari)
  useEffect(() => {
    if (hasPoster || !showPoster) return;
    const v = videoRef.current;
    if (!v) return;
    const showFrame = () => { v.currentTime = 0.01; };
    v.addEventListener('loadedmetadata', showFrame, { once: true });
    return () => { v.removeEventListener('loadedmetadata', showFrame); };
  }, [hasPoster, showPoster]);
  const [playerState, setPlayerState] = useState<VideoPlayerState>({
    isPlaying: false, currentTime: 0, duration: 0,
    volume: 1, isMuted: false, isEnded: false, isReady: false, playbackRate: 1,
  });

  const { notifyPlay } = useGlobalVideoPause(
    useCallback(() => { videoRef.current?.pause(); }, [])
  );

  // Native video events -> state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const lastUpdate = { time: 0 };

    const onTimeUpdate = () => {
      const now = Date.now();
      if (now - lastUpdate.time < 250) return;
      lastUpdate.time = now;
      setPlayerState((prev) => ({
        ...prev, isPlaying: !video.paused && !video.ended,
        currentTime: video.currentTime, duration: video.duration || 0,
      }));
    };

    const onReady = () => { setPlayerState((prev) => ({ ...prev, isReady: true, duration: video.duration || 0 })); };
    const onEnded = () => { setPlayerState((prev) => ({ ...prev, isEnded: true, isPlaying: false })); setShowPoster(true); };
    const onPlayPause = () => { setPlayerState((prev) => ({ ...prev, isPlaying: !video.paused && !video.ended })); };
    const onVolumeChange = () => { setPlayerState((prev) => ({ ...prev, volume: video.volume, isMuted: video.muted })); };

    if (video.readyState >= 1) onReady();

    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('canplay', onReady);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('play', onPlayPause);
    video.addEventListener('pause', onPlayPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('volumechange', onVolumeChange);

    return () => {
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('play', onPlayPause);
      video.removeEventListener('pause', onPlayPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('volumechange', onVolumeChange);
    };
  }, []);

  // Slide change: pause when leaving, resume when returning
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!isActive && !video.paused) {
      video.pause();
    } else if (isActive && autoPlay && !showPoster && video.paused) {
      notifyPlay();
      video.play().catch(() => {
      /* autoplay rejection is expected & non-fatal */
    });
    }
  }, [isActive, autoPlay, showPoster, notifyPlay]);

  // Auto-play on slide enter (first time with poster). Legitimate effect:
  // starts playback when the carousel slide becomes active (external isActive
  // prop); the setState syncs React's render to the video widget.
  useEffect(() => {
    if (isActive && autoPlay && showPoster) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync render to playback on slide activation
      setShowPoster(false);
      notifyPlay();
      onPlay?.();
      requestAnimationFrame(() => { videoRef.current?.play().catch(() => {
      /* autoplay rejection is expected & non-fatal */
    }); });
    }
  }, [isActive, autoPlay, showPoster, notifyPlay, onPlay]);

  const playerHandle = useMemo<VideoPlayerHandle>(() => ({
    play: () => { videoRef.current?.play().catch(() => {
      /* autoplay rejection is expected & non-fatal */
    }); notifyPlay(); onPlay?.(); },
    pause: () => videoRef.current?.pause(),
    seek: (time: number) => { if (videoRef.current) videoRef.current.currentTime = time; },
    setVolume: (vol: number) => { if (videoRef.current) videoRef.current.volume = vol; },
    setMuted: (muted: boolean) => { if (videoRef.current) videoRef.current.muted = muted; },
    setPlaybackRate: (rate: number) => {
      if (videoRef.current) videoRef.current.playbackRate = rate;
      setPlayerState((prev) => ({ ...prev, playbackRate: rate }));
    },
    requestFullscreen: () => {
      const v = videoRef.current;
      if (!v) return;
      // lib.dom types requestFullscreen as always present, but older iOS
      // Safari only offers webkitEnterFullscreen — Omit the lib member and
      // re-declare both as optional so the feature detection is honest
      // (a plain intersection would keep the required lib member).
      const fs = v as Omit<HTMLVideoElement, 'requestFullscreen'> & {
        requestFullscreen?: () => Promise<void>;
        webkitEnterFullscreen?: () => void;
      };
      if (fs.requestFullscreen) void fs.requestFullscreen();
      else if (fs.webkitEnterFullscreen) fs.webkitEnterFullscreen();
    },
    supportsFullscreen: true,
    requestPip: async () => {
      const v = videoRef.current;
      if (!v?.requestPictureInPicture) return;
      try {
        if (document.pictureInPictureElement) await document.exitPictureInPicture();
        await v.requestPictureInPicture();
      } catch { /* user denied or not supported */ }
    },
  }), [notifyPlay, onPlay]);

  const startPlayback = useCallback(() => {
    setShowPoster(false);
    notifyPlay();
    onPlay?.();
    requestAnimationFrame(() => { videoRef.current?.play().catch(() => {
      /* autoplay rejection is expected & non-fatal */
    }); });
  }, [notifyPlay, onPlay]);

  const videoUrl = video?.[0]?.url;
  if (!videoUrl) return null;

  return (
    <div className="relative aspect-video w-full overflow-hidden bg-black">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- TODO(captions): this content video has no <track> because the TYPO3 schema does not model caption sources yet. Add caption sources to the schema + a <track kind="captions"> here for WCAG AA once the CMS provides them. */}
      <video
        ref={videoRef}
        className={cn('absolute inset-0 size-full cursor-pointer object-cover', showPoster && hasPoster && 'invisible')}
        playsInline
        preload={hasPoster ? 'none' : 'metadata'}
        aria-label={emptyToUndefined(title)}
        onClick={() => {
          const v = videoRef.current;
          if (!v) return;
          if (v.paused) { notifyPlay(); v.play().catch(() => {
      /* autoplay rejection is expected & non-fatal */
    }); }
          else v.pause();
        }}
      >
        {/* video is narrowed non-null by the videoUrl early-return above */}
        {video.map((file, index) => (
          <source key={`${file.url}-${index}`} src={file.url} type={file.mimeType} />
        ))}
      </video>

      <button
        type="button"
        className={cn(
          'absolute inset-0 z-10 flex w-full cursor-pointer items-center justify-center border-none bg-transparent transition-opacity duration-500',
          showPoster ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={startPlayback}
        aria-label="Video abspielen"
        data-track-id={trackingPrefix ? `${trackingPrefix}-play` : undefined}
      >
        {hasPoster && (
          <RespImage
            srcsetCandidates={previewImage.srcsetCandidates as { imageUrl: string; width: number; height: number }[]}
            alt={emptyToUndefined(previewImage.altText) ?? emptyToUndefined(title) ?? ''}
            sizes={imageSizes}
            priority={priority}
            className="absolute inset-0 size-full object-cover"
          />
        )}
        <span className="relative z-10 flex size-16 items-center justify-center rounded-full bg-overlay-glass-strong text-white backdrop-blur-md transition-transform hover:scale-110">
          <Play className="size-5 fill-current" />
        </span>
      </button>

      {!showPoster && playerState.isReady && (
        <div className="absolute inset-x-0 bottom-0 z-20">
          <VideoControls playerHandle={playerHandle} playerState={playerState} videoType="video" />
        </div>
      )}
    </div>
  );
}
